import type { FxToastDismissDetail } from "../components/toast/toast";
import type { TThemeColors } from "../interface";

export interface FxToastElement extends HTMLElement {
  header: string;
  message: string;
  color?: TThemeColors;
  duration: number;
  closeButton: boolean;
  enterAnimation?: (baseEl: HTMLElement, opts?: any) => Animation;
  leaveAnimation?: (baseEl: HTMLElement, opts?: any) => Animation;
  isOpen: boolean;
  keyboardClose: boolean;
  position: ToastPosition;
  splash: boolean;
  splashDuration: number;

  componentOnReady(): Promise<this>;
  present(): Promise<void>;
  dismiss(): Promise<FxToastDismissDetail | undefined>;
  onDidDismiss(): Promise<FxToastDismissDetail>;
  onWillDismiss(): Promise<FxToastDismissDetail>;
}

export interface ToastOptions {
  header?: string;
  message?: string;
  color?: TThemeColors;
  duration?: number;
  closeButton?: boolean;
  enterAnimation?: (baseEl: HTMLElement, opts?: any) => Animation;
  leaveAnimation?: (baseEl: HTMLElement, opts?: any) => Animation;
  keyboardClose?: boolean;
  position?: ToastPosition;
  splash?: boolean;
  splashDuration?: number;

  /**
   * Maximum number of toasts displayed at the same time for this position.
   * Older toasts are dismissed when the limit is reached. `0` disables the limit.
   */
  max?: number;

  /**
   * Distance in pixels from the viewport edge used by the toast stack.
   */
  stackOffset?: number;

  /**
   * Gap in pixels between stacked toasts.
   */
  stackGap?: number;
}

type ToastPosition = "bottom" | "middle" | "top";

const TAG_NAME = "fx-toast";

const DEFAULT_MAX = 5;
const DEFAULT_OFFSET = 16;
const DEFAULT_GAP = 12;

function defaultEnterAnimation(
  baseEl: HTMLElement,
  opts?: { position?: ToastPosition },
): Animation {
  const position = opts?.position ?? "bottom";
  const fromY = position === "top" ? "-20px" : "20px";

  return baseEl.animate(
    [
      { opacity: 0, transform: `translateY(${fromY})` },
      { opacity: 1, transform: "translateY(0)" },
    ],
    { duration: 300, easing: "ease" },
  );
}

function defaultLeaveAnimation(
  baseEl: HTMLElement,
  opts?: { position?: ToastPosition },
): Animation {
  const position = opts?.position ?? "bottom";
  const toY = position === "top" ? "-20px" : "20px";

  return baseEl.animate(
    [
      { opacity: 1, transform: "translateY(0)" },
      { opacity: 0, transform: `translateY(${toY})` },
    ],
    { duration: 300, easing: "ease" },
  );
}

function applyOptions(toast: FxToastElement, options: ToastOptions): void {
  if (options.header !== undefined) toast.header = options.header;
  if (options.message !== undefined) toast.message = options.message;
  if (options.color !== undefined) toast.color = options.color;
  if (options.duration !== undefined) toast.duration = options.duration;
  if (options.closeButton !== undefined) toast.closeButton = options.closeButton;
  if (options.enterAnimation !== undefined) toast.enterAnimation = options.enterAnimation;
  if (options.leaveAnimation !== undefined) toast.leaveAnimation = options.leaveAnimation;
  if (options.keyboardClose !== undefined) toast.keyboardClose = options.keyboardClose;
  if (options.position !== undefined) toast.position = options.position;
  if (options.splash !== undefined) toast.splash = options.splash;
  if (options.splashDuration !== undefined) toast.splashDuration = options.splashDuration;
}

async function buildFxToast(options: ToastOptions = {}): Promise<FxToastElement> {
  if (typeof document === "undefined") {
    throw new Error("fx-toast can only be created in a browser environment");
  }

  await customElements.whenDefined(TAG_NAME);

  const position = options.position ?? "bottom";
  const toast = document.createElement(TAG_NAME) as FxToastElement;

  applyOptions(toast, options);
  toast.position = position;

  if (options.enterAnimation === undefined) {
    toast.enterAnimation = (baseEl, opts) =>
      defaultEnterAnimation(baseEl, opts ?? { position });
  }

  if (options.leaveAnimation === undefined) {
    toast.leaveAnimation = (baseEl, opts) =>
      defaultLeaveAnimation(baseEl, opts ?? { position });
  }

  return toast;
}

class FxToastStack {
  el: HTMLDivElement | undefined;

  private toasts: FxToastElement[] = [];
  private rafId?: number;
  private presentQueue: Promise<void> = Promise.resolve();
  private mutationObserver?: MutationObserver;
  private resizeObserver?: ResizeObserver;

  constructor(
    public position: ToastPosition,
    public max: number,
    public offset: number,
    public gap: number,
  ) {
    if (typeof document === "undefined") {
      return;
    }

    this.el = document.createElement("div");
    this.el.className = "fx-toast-stack";
    this.el.setAttribute("data-position", position);
    this.el.setAttribute("aria-live", "polite");
    this.el.setAttribute("aria-atomic", "false");
    document.body.appendChild(this.el);

    this.mutationObserver = new MutationObserver(this.handleMutations);
    this.mutationObserver.observe(this.el, { childList: true });

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.handleResizes);
    }
  }

  enqueue(toast: FxToastElement): void {
    this.el?.appendChild(toast);
    this.toasts.push(toast);
    this.resizeObserver?.observe(toast);

    toast.addEventListener("fxDidPresent", this.handlePresent);
    toast.addEventListener("fxDidDismiss", this.handleDismiss);
  }

  async present(toast: FxToastElement): Promise<void> {
    this.presentQueue = this.presentQueue.then(async () => {
      if (this.max > 0) {
        const visible = this.toasts.filter(
          (t) =>
            t.hasAttribute("data-presented") &&
            !t.hasAttribute("data-dismissing") &&
            t !== toast,
        );

        if (visible.length >= this.max) {
          const oldest = visible[0];
          await oldest.dismiss();
        }
      }

      await toast.present();
    });

    return this.presentQueue;
  }

  private handlePresent = () => this.scheduleLayout();

  private handleDismiss = (event: Event) => {
    const toast = event.target as FxToastElement;
    this.detach(toast);
    this.scheduleLayout();

    // Keep the dismissed toast in the DOM briefly so the remaining toasts can
    // animate into their new positions before it is removed.
    window.setTimeout(() => {
      if (toast.parentNode === this.el) {
        toast.remove();
      }
    }, 350);
  };

  private detach(toast: FxToastElement): void {
    const index = this.toasts.indexOf(toast);
    if (index > -1) {
      this.toasts.splice(index, 1);
    }

    this.resizeObserver?.unobserve(toast);
    toast.removeEventListener("fxDidPresent", this.handlePresent);
    toast.removeEventListener("fxDidDismiss", this.handleDismiss);
  }

  private handleMutations = (records: MutationRecord[]) => {
    let changed = false;

    for (const record of records) {
      for (const node of record.removedNodes) {
        const toast = node as FxToastElement;
        if (this.toasts.includes(toast)) {
          this.detach(toast);
          changed = true;
        }
      }
    }

    if (changed) {
      this.scheduleLayout();
    }
  };

  private handleResizes = () => this.scheduleLayout();

  private scheduleLayout(): void {
    if (this.rafId !== undefined) {
      return;
    }

    if (typeof requestAnimationFrame === "undefined") {
      this.layout();
      return;
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = undefined;
      this.layout();
    });
  }

  private layout(): void {
    if (typeof window === "undefined" || !this.el) {
      return;
    }

    const toasts = this.toasts.filter((t) => t.isConnected);
    const heights = toasts.map((t) => t.offsetHeight);
    const viewportHeight = window.innerHeight;
    const { position, offset, gap } = this;

    let current = 0;

    if (position === "top") {
      current = offset;
      for (let i = 0; i < toasts.length; i++) {
        toasts[i].style.top = `${current}px`;
        current += heights[i] + gap;
      }
    } else if (position === "bottom") {
      current = offset;
      for (let i = toasts.length - 1; i >= 0; i--) {
        toasts[i].style.top = `${viewportHeight - current - heights[i]}px`;
        current += heights[i] + gap;
      }
    } else {
      // middle
      const total =
        heights.reduce((sum, height) => sum + height, 0) +
        gap * Math.max(0, toasts.length - 1);
      current = (viewportHeight - total) / 2;
      for (let i = 0; i < toasts.length; i++) {
        toasts[i].style.top = `${current}px`;
        current += heights[i] + gap;
      }
    }
  }
}

const stacks = new Map<ToastPosition, FxToastStack>();

function getOrCreateStack(
  position: ToastPosition,
  max?: number,
  offset?: number,
  gap?: number,
): FxToastStack {
  const existing = stacks.get(position);
  if (existing) {
    if (max !== undefined) existing.max = max;
    if (offset !== undefined) existing.offset = offset;
    if (gap !== undefined) existing.gap = gap;
    return existing;
  }

  const stack = new FxToastStack(
    position,
    max ?? DEFAULT_MAX,
    offset ?? DEFAULT_OFFSET,
    gap ?? DEFAULT_GAP,
  );
  stacks.set(position, stack);
  return stack;
}

async function createFxToast(options: ToastOptions = {}): Promise<FxToastElement> {
  const toast = await buildFxToast(options);
  const stack = getOrCreateStack(
    toast.position,
    options.max,
    options.stackOffset,
    options.stackGap,
  );
  stack.enqueue(toast);
  await stack.present(toast);
  return toast;
}

async function createFxToastElement(
  options: ToastOptions = {},
): Promise<FxToastElement> {
  const toast = await buildFxToast(options);

  // Keep the deprecated controller.create path out of the queue so consumers
  // can present / dismiss / re-present the same element manually without it
  // being removed from the DOM by the stack's dismiss cleanup.
  document.body.appendChild(toast);

  if (typeof toast.componentOnReady === "function") {
    await toast.componentOnReady();
  }

  return toast;
}

async function dismissFxToast(toast?: FxToastElement): Promise<void> {
  if (toast) {
    await toast.dismiss();
    return;
  }

  const active = document.querySelector(
    `${TAG_NAME}[data-presented]`,
  ) as FxToastElement | null;

  if (active) {
    await active.dismiss();
  }
}

/**
 * Creates and presents a toast inside a position-aware stack. The stack makes
 * sure toasts do not overlap, respects the `max` option, and animates the
 * remaining toasts into the newly available space when one is dismissed.
 */
export async function createToast(
  options: ToastOptions = {},
): Promise<FxToastElement> {
  return createFxToast(options);
}

/**
 * @deprecated Use the `createToast` function instead.
 */
export const toastController = {
  create: createFxToastElement,
  present: createFxToast,
  show: createFxToast,
  dismiss: dismissFxToast,
};

export type { FxToastDismissDetail };
